import { Button, Empty, Form, Input, Timeline, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DynamicObjectType } from 'types/commons';
import { formatDateTime } from 'utils/formattedString';
import { useDispatch, useSelector } from '@redux';
import { CardWrapper } from './styles';
import { RETURN_STATUS } from 'constants/ordersReturn';
import { TFilterRequest } from 'core/types/common';
import { getAllApi } from 'api/crud';
import { formatFilter } from '@redux/crudCreator/dataProvider';
import { createOrderReturnHistories } from '@redux/orderReturnHistories/actions';
import { useRestContext } from 'components/form/RestInputContext';
import styled from 'styled-components';

const getStatusText = ({ item }) => {
  return item?.text;
};

const OrderHistories = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { record } = useRestContext();
  const [form] = Form.useForm();
  const [dataHistories, setDataHistories] = useState<DynamicObjectType[]>([]);

  const currentUser = useSelector(
    (state) => state.auth.data,
  ) as DynamicObjectType;

  const handleGetHistories = useCallback(async (id) => {
    if (id) {
      const filters = [
        {
          condition: 'and',
          key: 'returnId',
          operator: 'is',
          value: id,
        },
      ] as TFilterRequest[];
      const data = await getAllApi('order-return-histories', {
        join: 'creator',
        limit: 99,
        s: JSON.stringify(formatFilter(filters || [])),
        sort: 'createdAt,ASC',
      });
      if (Array.isArray(data?.data) || data?.data?.length > 0) {
        setDataHistories(data?.data);
      } else {
        setDataHistories([]);
      }
    }
  }, []);

  const onSubmit = (values) => {
    dispatch(
      createOrderReturnHistories({
        data: {
          ...values,
          status: record?.status,
          returnId: Number(record?.id),
          creatorId: currentUser?.id,
        },
      }),
    ).then(() => {
      form.resetFields();
      handleGetHistories(record?.id);
    });
  };

  const getHistories = useMemo(() => {
    return () => {
      if (dataHistories?.length > 0) {
        return [...dataHistories].reverse().map((e) => {
          const currentStatus = RETURN_STATUS[e.status];
          return {
            children: (
              <div>
                <div className="time-point">{formatDateTime(e.createdAt)}</div>
                <div className="font-semibold">
                  {t(getStatusText({ item: currentStatus }))}
                </div>
                <Tooltip
                  title={
                    e?.creatorId
                      ? `${e.creator?.firstName || ''} ${e.creator?.lastName || ''}: ${e?.comment || ''}`
                      : `${e?.comment || ''} `
                  }
                >
                  <div className="text-secondary mt-[10px] line-clamp-2">
                    {e?.creatorId
                      ? `${e.creator?.firstName || ''} ${e.creator?.lastName || ''}: ${e?.comment || ''}`
                      : `${e?.comment || ''} `}
                  </div>
                </Tooltip>
                {e?.note && <div className="mt-1">Note: {e?.note}</div>}
              </div>
            ),
          };
        });
      }
      return [];
    };
  }, [dataHistories]);

  useEffect(() => {
    handleGetHistories(record?.id);
  }, [record?.id, record?.status]);

  return (
    <CardWrapper title={t('ordersReturn.histories')}>
      <Form form={form} onFinish={onSubmit}>
        <div className="d-flex w-full mt-[10px]">
          <Form.Item name="comment" noStyle>
            <Input
              placeholder="Add comment"
              suffix={
                <Button
                  htmlType="button"
                  type="primary"
                  onClick={() => form.submit()}
                >
                  {t('button.add')}
                </Button>
              }
            />
          </Form.Item>
        </div>
      </Form>

      {dataHistories?.length > 0 ? (
        <TimelineStyles
          className="mt-[40px]"
          mode="left"
          items={getHistories()}
        ></TimelineStyles>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </CardWrapper>
  );
};

const TimelineStyles = styled(Timeline)`
  padding-left: 150px;

  .time-point {
    position: absolute;
    left: -160px;
  }
`;

export default OrderHistories;
